// Priority+ tab-overflow logic (Design Bible D17, frame 5j) — the pure part,
// unit-tested independently of DOM measurement.
//
// Rule set (5j, verbatim behaviour):
//   • The active tab is NEVER hidden inside More. If it would overflow, it swaps
//     into the LAST visible slot (5j: DM · Last Minute · Club Req.[active] with
//     Agent Req. + Scout Req. in More — Club Req. took Agent Req.'s slot).
//   • The More button carries the AGGREGATED unread of its hidden tabs (5j: 2+1=3).
//   • Mobile never overflows (full-label swipe-scroll) — the caller passes a
//     visibleCount ≥ tabs.length there so hidden stays empty.
//
// computeTabOverflow(tabs, activeKey, visibleCount):
//   tabs         [{ key, label, count? }] in canonical order
//   activeKey    currently selected tab key
//   visibleCount number of REAL-tab slots available beside the More button
//                (i.e. how many tabs physically fit). When tabs.length exceeds
//                it, one slot is implicitly the More button.
// → { visible: [...tabs], hidden: [...tabs], hiddenCount: number }
export const computeTabOverflow = (tabs = [], activeKey = null, visibleCount = Infinity) => {
  const list = Array.isArray(tabs) ? tabs : [];
  const cap = Math.max(1, Math.floor(visibleCount));

  if (list.length <= cap) {
    return { visible: list, hidden: [], hiddenCount: 0 };
  }

  // Overflow: reserve the last slot for the More button → (cap - 1) real slots.
  const realSlots = Math.max(1, cap - 1);
  let visible = list.slice(0, realSlots);
  let hidden = list.slice(realSlots);

  // Keep the active tab visible: if it landed in `hidden`, swap it into the last
  // visible slot (5j's "swaps it with the last visible tab").
  if (activeKey != null && !visible.some((t) => t.key === activeKey)) {
    const activeIdx = hidden.findIndex((t) => t.key === activeKey);
    if (activeIdx !== -1) {
      const lastVisibleIdx = visible.length - 1;
      const activeTab = hidden[activeIdx];
      const demoted = visible[lastVisibleIdx];
      visible = [...visible];
      hidden = [...hidden];
      visible[lastVisibleIdx] = activeTab;
      hidden[activeIdx] = demoted;
    }
  }

  const hiddenCount = hidden.reduce((sum, t) => sum + (t.count || 0), 0);
  return { visible, hidden, hiddenCount };
};

// computeFitCount(widths, avail, opts) — the DOM-measurement half of the D17
// overflow, kept pure so the gap arithmetic is unit-testable.
//
// Given the natural per-tab widths and the row's available width, returns the
// slot count to feed computeTabOverflow as `visibleCount` (More counts as the
// last slot). The critical detail: the strip is laid out with a fixed gap
// BETWEEN tabs, so the real occupied width is Σwidths + gap·(n−1). Ignoring the
// gap (the original bug) made the row measure ~56px narrower than it renders and
// the overflow check passed while the tabs actually clipped, so "More ▾" never
// appeared when the window shrank into the md column.
//
//   widths     [number] natural widths of the tabs, in canonical order
//   avail      container width in px
//   opts.gap   inter-tab gap in px (mirror the row's gap-[14px]) — default 14
//   opts.moreWidth  width reserved for the More ▾ button once overflowing — 64
// → Infinity when unmeasurable (jsdom / a zero-width tab) so callers render all.
export const computeFitCount = (
  widths = [],
  avail = 0,
  { gap = 14, moreWidth = 64 } = {},
) => {
  const ws = Array.isArray(widths) ? widths : [];
  if (!avail || ws.length === 0 || ws.some((w) => !(w > 0))) return Infinity;

  const gaps = gap * (ws.length - 1);
  const total = ws.reduce((a, w) => a + w, 0) + gaps;
  if (total <= avail) return ws.length; // all fit → no More

  // Overflowing → More ▾ shows; fit tabs into what's left after reserving it
  // (its width + the gap before it), each tab after the first paying its gap.
  const budget = avail - moreWidth - gap;
  let used = 0;
  let n = 0;
  for (let i = 0; i < ws.length; i += 1) {
    const w = ws[i] + (i > 0 ? gap : 0);
    if (used + w > budget) break;
    used += w;
    n += 1;
  }
  return Math.max(1, n) + 1; // + the More slot
};

export default computeTabOverflow;
